// One request from the website's contact form: what the visitor wrote, a reply by email in their language, and where
// staff are with it (status and an internal note, `PATCH /admin/contact-requests/{id}`).
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime } from '../../services/format';
import { Alert, Button, Card, Field, KeyValue, PageTitle, Select, Textarea } from '../../components/ui';
import type { ContactStatus } from '../../types/api';
import { CONTACT_STATUSES, ContactStatusBadge } from './common';

export function ContactRequestDetail() {
  const { id } = useParams();
  const requestId = Number(id);
  const { t, i18n } = useTranslation();
  const { data: request, error, reload } = useAsync(() => api.contactRequest(requestId), [requestId]);
  const [status, setStatus] = useState<ContactStatus>('new');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!request) return;
    setStatus(request.status);
    setNote(request.note ?? '');
  }, [request]);

  if (error) return <Alert>{error}</Alert>;
  if (!request) return <p className="text-faint">{t('common.loading')}</p>;

  const changed = status !== request.status || note.trim() !== (request.note ?? '');
  const save = async () => {
    setBusy(true);
    setFail(null);
    setSaved(false);
    try {
      await api.updateContactRequest(request.id, { status, note });
      setSaved(true);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // The reply opens in the staff member's mail client, its subject in the visitor's language.
  const subject = t('contact.replySubject', { lng: request.lang });
  const reply = `mailto:${request.email}?subject=${encodeURIComponent(subject)}`;

  return (
    <div>
      <p className="mb-2 text-[12px]">
        <Link to="/contact-requests" className="text-muted hover:text-ink">
          ← {t('contact.title')}
        </Link>
      </p>
      <PageTitle
        title={
          <>
            {request.name} <ContactStatusBadge status={request.status} />
          </>
        }
        subtitle={
          <span className="font-mono">
            #{request.id} · {t(`contact.roles.${request.role}`, request.role)} · {dateTime(request.created_at, i18n.language)}
          </span>
        }
        actions={
          <a href={reply} className="inline-flex h-8 items-center rounded-[5px] bg-accent px-3 text-[12.5px] font-medium text-white hover:bg-accent-hover" data-testid="contact-reply">
            {t('contact.reply')}
          </a>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-4">
          <Card title={t('contact.message')}>
            {request.message ? (
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink" data-testid="contact-message">
                {request.message}
              </p>
            ) : (
              <p className="text-[12.5px] text-faint">{t('contact.noMessage')}</p>
            )}
          </Card>
          <Card title={t('contact.visitor')}>
            <KeyValue
              rows={[
                [t('contact.name'), request.name],
                [t('contact.email'), <a key="e" href={reply} className="font-mono text-[11.5px] text-accent hover:underline">{request.email}</a>],
                [t('contact.role'), t(`contact.roles.${request.role}`, request.role)],
                [t('contact.company'), request.company ?? '—'],
                [t('contact.lang'), <span key="l" className="font-mono text-[11.5px]">{request.lang}</span>],
                [t('contact.page'), <span key="p" className="font-mono text-[11.5px]">{request.page ?? '—'}</span>],
                [t('contact.receivedAt'), dateTime(request.created_at, i18n.language)],
              ]}
            />
          </Card>
        </div>
        <Card title={t('contact.handling')}>
          <div className="flex flex-col gap-3">
            {fail && <Alert>{fail}</Alert>}
            {saved && !changed && <Alert tone="good">{t('common.saved')}</Alert>}
            <Field label={t('contact.statusLabel')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value as ContactStatus)} data-testid="contact-status">
                {CONTACT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`contact.status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('contact.note')} hint={t('contact.noteHint')}>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={5} maxLength={4000} data-testid="contact-note" />
            </Field>
            <div>
              <Button variant="primary" busy={busy} disabled={!changed} onClick={() => void save()} data-testid="contact-save">
                {t('common.save')}
              </Button>
            </div>
            <KeyValue
              rows={[
                [t('contact.handledBy'), request.handled_by_email ?? '—'],
                [t('contact.handledAt'), dateTime(request.handled_at, i18n.language)],
              ]}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
